import React, { createContext, useContext } from 'react';

type DynamicModulesContextType = Readonly<{
	isDynamicModuleLoaded: (moduleName: DynamicModuleName) => boolean;
	markDynamicModuleAsLoaded: (moduleName: DynamicModuleName) => void;
}>;

type DynamicModuleName =
	| 'Recommendations'
	| 'ReadyToShare'
	| 'ReadyToSharePreDrafted'
	| 'PetitionInsights'
	| 'StepsToVictory'
	| 'ShareVisual'
	| 'SocialMediaContent'
	| 'SharingTools';

const DynamicModulesContext = createContext<DynamicModulesContextType>({
	isDynamicModuleLoaded: () => false,
	markDynamicModuleAsLoaded: () => {},
});

export const useDynamicModules = () => useContext(DynamicModulesContext);

/**
 * Custom hook to automatically mark a dynamic module as loaded when a condition is met
 * @param moduleName - The name of the module to mark as loaded
 * @param shouldMarkAsLoaded - Boolean condition that determines when the module should be marked as loaded
 */
export const useMarkModuleAsLoaded = (moduleName: DynamicModuleName, shouldMarkAsLoaded: boolean): void => {
	const { markDynamicModuleAsLoaded } = useDynamicModules();

	React.useEffect(() => {
		if (shouldMarkAsLoaded) {
			markDynamicModuleAsLoaded(moduleName);
		}
	}, [shouldMarkAsLoaded, moduleName, markDynamicModuleAsLoaded]);
};

export const DynamicModulesProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [loadedModules, setLoadedModules] = React.useState<Set<DynamicModuleName>>(new Set());

	const isDynamicModuleLoaded = (moduleName: DynamicModuleName): boolean => {
		return loadedModules.has(moduleName);
	};

	const markDynamicModuleAsLoaded = (moduleName: DynamicModuleName): void => {
		setLoadedModules((prev) => {
			if (prev.has(moduleName)) {
				return prev;
			}
			return new Set(prev).add(moduleName);
		});
	};

	return (
		<DynamicModulesContext.Provider
			value={{ isDynamicModuleLoaded, markDynamicModuleAsLoaded }}
			data-qa="dynamic-modules-context"
		>
			{children}
		</DynamicModulesContext.Provider>
	);
};
